if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');

        // PERFORMANCE (Mantos PH): a miniatura ativa era atualizada com debounce de 500ms
        // depois do evento 'slideChanged'. Agora deriva do MESMO índice do carrossel principal
        // (event.detail.currentElement) e atualiza no próximo frame, junto com foto e contador.
        // Eventos rápidos em sequência são agrupados por requestAnimationFrame (sem espera artificial).
        this.pendingSlideEvent = null;
        this.slideFrame = null;
        if (this.elements.viewer) {
          this.elements.viewer.addEventListener('slideChanged', (event) => {
            this.pendingSlideEvent = event;
            if (this.slideFrame) return;
            this.slideFrame = requestAnimationFrame(() => {
              this.slideFrame = null;
              this.onSlideChanged(this.pendingSlideEvent);
            });
          });
          this.warmUpAdjacentMedia();
        }

        if (!this.elements.thumbnails) return;

        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        if (!event || !event.detail || !event.detail.currentElement) return;
        const currentElement = event.detail.currentElement;
        this.preloadAround(currentElement);
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      // Preload inteligente: garante que a foto anterior e a próxima da atual já estejam
      // carregando/prontas. Fotos distantes continuam com lazy loading.
      preloadAround(mediaElement) {
        if (!mediaElement || !mediaElement.parentElement) return;
        const items = Array.from(mediaElement.parentElement.querySelectorAll(':scope > [data-media-id]'));
        const index = items.indexOf(mediaElement);
        if (index < 0) return;
        [index - 1, index + 1].forEach((i) => {
          const item = items[i];
          if (!item) return;
          const img = item.querySelector('.product__modal-opener--image img');
          if (img && img.loading === 'lazy') img.loading = 'eager';
        });
      }

      // Após o carregamento inicial (quando o navegador estiver ocioso), deixa a 2ª foto pronta,
      // sem competir com a primeira imagem nem com o restante da página.
      warmUpAdjacentMedia() {
        const run = () => {
          const active =
            this.elements.viewer.querySelector('[data-media-id].is-active') ||
            this.elements.viewer.querySelector('[data-media-id]');
          this.preloadAround(active);
        };
        const schedule = () => {
          if ('requestIdleCallback' in window) {
            window.requestIdleCallback(run, { timeout: 2000 });
          } else {
            run();
          }
        };
        if (document.readyState === 'complete') {
          schedule();
        } else {
          window.addEventListener('load', schedule, { once: true });
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preloadAround(activeMedia);
        const activeImg = activeMedia.querySelector('.product__modal-opener--image img');
        if (activeImg && activeImg.loading === 'lazy') activeImg.loading = 'eager';

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          // Mantos PH: TROCA DE VARIANTE (prepend = true) NÃO rola a página.
          // O Dawn original levava a tela até a foto sempre que a variante mudava (tamanho,
          // personalização, patrocínio...), "chutando" o cliente para cima. A foto continua
          // trocando; só a posição da tela fica parada. A rolagem até a foto só acontece
          // quando o cliente toca numa miniatura (prepend = false).
          if (prepend) return;
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        const button = thumbnail.querySelector('button');
        if (button && button.getAttribute('aria-current') === 'true') return;

        this.elements.thumbnails
          .querySelectorAll('button[aria-current]')
          .forEach((element) => element.removeAttribute('aria-current'));
        if (button) button.setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        const announce = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        // Antes: "image.src = image.src" forçava o navegador a reprocessar a imagem a cada toque
        // na miniatura. Agora só anuncia quando a imagem já está pronta (ou assim que carregar).
        if (image.complete && image.naturalWidth > 0) {
          announce();
        } else {
          image.addEventListener('load', announce, { once: true });
        }
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
